import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Popover } from './Popover.js'
import { Input } from './Input.js'
import { Text } from './Typography.js'

export interface ComboboxItem {
  value: string
  label: string
  /** A second line — an email, a city. What tells two people with one name apart. */
  detail?: string
}

export interface ComboboxProps {
  items: ComboboxItem[]
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  /** Shown while the list is still being fetched. */
  loading?: boolean
  /**
   * Given, the **caller** narrows the list and this stops filtering.
   *
   * A directory does not fit in a request. `GET /providers` caps at a hundred,
   * so a combobox that filters what it was handed can only ever find the
   * first hundred professionals — which is the unreachable-row defect the
   * support inbox had, moved into a dropdown. With this, the typing goes to
   * the server and the list is whatever came back.
   */
  onQueryChange?: (query: string) => void
  /** What to say when the filter matches nothing. Name what to try instead. */
  emptyText?: string
  clearable?: boolean
  invalid?: boolean
  disabled?: boolean
  id?: string
  'aria-describedby'?: string
}

/**
 * Pick one record out of many, by typing.
 *
 * Seventy request fields are an id — `host_provider_id`, `category_id`,
 * `city_id`, `cover_asset_id`. A `Select` is right for a handful and wrong for a
 * directory: 9.6a asks for *"the host"*, and a marketplace of professionals in a
 * dropdown is a list somebody scrolls past the person they wanted.
 *
 * The rule between the two: a `Select` when every option fits on a screen, this
 * when it does not.
 */
export function Combobox({
  items,
  value,
  onChange,
  placeholder = 'Choose one',
  loading,
  onQueryChange,
  emptyText = 'Nothing matched. Try fewer letters.',
  clearable,
  invalid,
  disabled,
  id,
  ...aria
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  /**
   * Which one Enter would take.
   *
   * Without it this was type-to-filter and then **Tab through the results** —
   * so the thing everybody does, typing three letters and pressing Enter, did
   * nothing at all. Arrow keys move it, Enter takes it, Escape shuts the
   * list; typing resets it to the top, because the top is what the typing
   * just changed.
   */
  const [highlighted, setHighlighted] = useState(0)

  const chosen = items.find((i) => i.value === value) ?? null
  const matched = useMemo(() => {
    /* Somebody else is doing the narrowing. Filtering again here would hide
       rows the server deliberately returned — a search for "raman" that the
       server matched on an email address, and this would drop. */
    if (onQueryChange) return items
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (i) => i.label.toLowerCase().includes(q) || i.detail?.toLowerCase().includes(q),
    )
  }, [items, query, onQueryChange])
  /* Fifty drawn at a time, which is a long list to look at and a short one to
     render. What was missing was **saying so**: the list simply stopped, and
     somebody whose person was fifty-first concluded they were not there. */
  const shown = matched.slice(0, SHOWN_AT_ONCE)
  const hidden = matched.length - shown.length

  const take = (item: ComboboxItem | undefined) => {
    if (!item) return
    onChange(item.value)
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) {
          setQuery('')
          onQueryChange?.('')
        }
      }}
      className="w-(--radix-popover-trigger-width) p-0"
      trigger={
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-expanded={open}
          {...aria}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-md border bg-surface px-4 text-left text-body',
            'outline-none transition-colors focus-visible:border-coral-fill focus-visible:ring-2 focus-visible:ring-coral-fill/25',
            'disabled:bg-sunk disabled:text-ink-muted disabled:cursor-not-allowed',
            invalid ? 'border-error' : 'border-hairline-strong',
            !chosen && 'text-ink-muted',
          )}
        >
          <span className="flex-1 truncate">{chosen?.label ?? placeholder}</span>
          <ChevronsUpDown className="size-4 shrink-0 text-ink-muted" aria-hidden />
        </button>
      }
    >
      <div className="border-b border-hairline p-2">
        <Input
          autoFocus
          size="sm"
          value={query}
          role="combobox"
          aria-expanded
          aria-controls={`${id ?? 'combobox'}-list`}
          onChange={(e) => {
            setQuery(e.target.value)
            setHighlighted(0)
            onQueryChange?.(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setHighlighted((h) => Math.min(h + 1, shown.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setHighlighted((h) => Math.max(h - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              take(shown[highlighted])
            } else if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
          placeholder="Type to narrow it"
        />
      </div>
      <div id={`${id ?? 'combobox'}-list`} className="max-h-64 overflow-y-auto p-1" role="listbox">
        {loading ? (
          <Text variant="secondary" className="p-3">
            Loading.
          </Text>
        ) : shown.length === 0 ? (
          <Text variant="secondary" className="p-3">
            {emptyText}
          </Text>
        ) : (
          shown.map((i, at) => (
            <button
              key={i.value}
              type="button"
              role="option"
              aria-selected={i.value === value}
              onMouseEnter={() => setHighlighted(at)}
              onClick={() => take(i)}
              className={cn(
                'flex w-full items-start gap-2 rounded-sm px-3 py-2 text-left outline-none',
                'hover:bg-sunk focus-visible:bg-sunk',
                /* What Enter would take, marked. Without it the arrow keys
                   moved something invisible. */
                at === highlighted && 'bg-sunk',
              )}
            >
              <Check
                className={cn(
                  'mt-0.5 size-4 shrink-0',
                  i.value === value ? 'text-coral-fill' : 'opacity-0',
                )}
              />
              <span className="flex min-w-0 flex-col">
                <Text as="span" variant="body" className="truncate">
                  {i.label}
                </Text>
                {i.detail && (
                  <Text as="span" variant="caption" className="truncate">
                    {i.detail}
                  </Text>
                )}
              </span>
            </button>
          ))
        )}
      </div>
      {hidden > 0 && (
        <Text variant="caption" className="text-ink-muted border-hairline border-t px-3 py-2">
          {`${hidden} more match that. Type a little more to narrow it.`}
        </Text>
      )}
      {clearable && value && (
        <div className="border-t border-hairline p-1">
          <button
            type="button"
            onClick={() => {
              onChange(null)
              setOpen(false)
            }}
            className="w-full rounded-sm px-3 py-2 text-left text-second text-ink-2 hover:bg-sunk"
          >
            Clear the choice
          </button>
        </div>
      )}
    </Popover>
  )
}

/** How many to draw at once. See `hidden`, which says what is behind them. */
const SHOWN_AT_ONCE = 50
